import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/button";
import PageContainer from "../../components/page-container";
import CustomDropdown from "../../components/custom-dropdown";
import s from "./styles.module.scss";
import PageHeader from "../../components/page-header";
import { Loading } from "../loading";
import { useSections } from "../../hooks/use-sections";

const TimezoneSelection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { sections, updateSections } = useSections();
    const [selectedTimezones, setSelectedTimezones] = useState({});
    const [loading, setLoading] = useState(false);

    // CustomDropdown expects an array of strings
    const timezoneOptions = [
        "Eastern - America/New York",
        "Central - America/Chicago",
        "Mountain - America/Denver",
        "Pacific - America/Los Angeles",
        "Arizona - America/Phoenix",
        "Alaska - America/Anchorage",
        "Hawaii - Pacific/Honolulu"
];

    // Mapping for converting display labels to actual timezone values
    const timezoneMapping = {
        "Eastern - America/New York": "America/New_York",
        "Central - America/Chicago": "America/Chicago",
        "Mountain - America/Denver": "America/Denver",
        "Pacific - America/Los Angeles": "America/Los_Angeles",
        "Arizona - America/Phoenix": "America/Phoenix",
        "Alaska - America/Anchorage": "America/Anchorage",
        "Hawaii - Pacific/Honolulu": "Pacific/Honolulu"
    };


    // Get courses that need timezone selection
    const coursesNeedingTimezone = useMemo(() => {
        const sections = JSON.parse(localStorage.getItem('sections') || '{}');
        const courses = [];
        
        // Check from state if passed from previous screen
        if (location.state?.coursesWithoutTimezone) {
          return location.state.coursesWithoutTimezone;
        }
        
        // Check sections for courses without timezone
        Object.entries(sections).forEach(([fileName, sectionData]) => {
          // Check if timezone is null or undefined (regardless of sections)
          if (sectionData.timezone === null || sectionData.timezone === undefined) {
            courses.push({
              fileName,
              courseName: sectionData.course_name || fileName.replace('uploads/', ''),
              sections: sectionData.sections || []
            });
          }
        });
        
        return courses;
      }, [sections, location.state]);

  useEffect(() => {
    // Initialize timezone selections with default (ET)
    const initialTimezones = {};
    coursesNeedingTimezone.forEach(course => {
      initialTimezones[course.fileName] = 'America/New_York';
    });
    setSelectedTimezones(initialTimezones);
  }, [coursesNeedingTimezone]);

  const handleTimezoneChange = (fileName, selectedLabel) => {
    const timezoneValue = timezoneMapping[selectedLabel];
    setSelectedTimezones(prev => ({
      ...prev,
      [fileName]: timezoneValue
    }));
  };

  const getDisplayValue = (fileName) => {
    const value = selectedTimezones[fileName] || 'America/New_York';
    const label = Object.entries(timezoneMapping).find(([key, val]) => val === value);
        return label ? label[0] : "Eastern - America/New York";
  };

  const handleApplyToAll = () => {
    if (coursesNeedingTimezone.length === 0) return;
    
    // Get the first selected timezone
    const firstTimezone = selectedTimezones[coursesNeedingTimezone[0].fileName] || 'America/New_York';
    
    // Apply to all courses
    const updatedTimezones = {};
    coursesNeedingTimezone.forEach(course => {
      updatedTimezones[course.fileName] = firstTimezone;
    });
    
    setSelectedTimezones(updatedTimezones);
  };

  const handleConfirm = async () => {
    setLoading(true);
    
    try {
      // Get the sections from localStorage
      const sections = JSON.parse(localStorage.getItem('sections') || '{}');
      
      // Update each course with selected timezone
      Object.entries(selectedTimezones).forEach(([fileName, timezone]) => {
        if (sections[fileName]) {
          sections[fileName].timezone = timezone;
        }
      });
      
      // Update sections in context and localStorage
      updateSections(sections);
      localStorage.setItem('sections', JSON.stringify(sections));
      
      // Clear the timezone selection flag
      localStorage.removeItem('needs_timezone_selection');
      
      // Check if fallback is required
      const fallbackRequired = JSON.parse(localStorage.getItem('fallback_required') || 'false');
      
      // Navigate based on whether sections need manual selection
      if (fallbackRequired) {
        navigate('/choose-sections', { replace: true });
      } else {
        navigate('/select-sections', { replace: true });
      }
      
    } catch (error) {
      console.error('Error updating timezones:', error);
      alert('Error updating timezones. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <div className={s.container}>
        <PageHeader
          title="Select time zone for your classes"
          description="We couldn't automatically detect the timezone for some of your classes. Please select the appropriate timezone below."
        />

        <div className={s.timezoneList}>
          {coursesNeedingTimezone.length > 1 && (
            <div className={s.applyAllSection}>
              <Button
                variant="secondary"
                size="small"
                onClick={handleApplyToAll}
                className={s.applyAllButton}
              >
                Apply first time zone to all
              </Button>
            </div>
          )}

          {coursesNeedingTimezone.map((course) => (
            <div key={course.fileName} className={s.courseTimezoneRow}>
              <div className={s.courseInfo}>
                <span className={s.courseName}>{course.courseName}</span>
              </div>

              <div className={s.dropdownContainer}>
                <CustomDropdown
                  options={timezoneOptions}
                  value={getDisplayValue(course.fileName)}
                  onChange={(value) => handleTimezoneChange(course.fileName, value)}
                  placeholder="Select timezone"
                  disabled={loading}
                />
              </div>
            </div>
          ))}
        </div>

        <div className={s.formActions}>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={loading}
            className={s.confirmButton}
          >
            {loading ? 'Updating...' : 'Confirm timezones'}
          </Button>

        </div>
      </div>
    </PageContainer>
  );
};

export default TimezoneSelection;